import { createElement, forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../../utils';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingWeight = 'medium' | 'semibold' | 'bold';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level: HeadingLevel;
  /** Override del tag HTML sin cambiar el estilo visual. Útil para SEO. */
  as?: HeadingTag;
  /** @default bold para h1/h2, semibold para h3/h4, medium para h5/h6 */
  weight?: HeadingWeight;
}

const sizeStyles: Record<HeadingLevel, string> = {
  1: 'text-h1',
  2: 'text-h2',
  3: 'text-h3',
  4: 'text-h4',
  5: 'text-h5',
  6: 'text-h6',
};

const defaultWeightByLevel: Record<HeadingLevel, HeadingWeight> = {
  1: 'bold',
  2: 'bold',
  3: 'semibold',
  4: 'semibold',
  5: 'medium',
  6: 'medium',
};

const weightStyles: Record<HeadingWeight, string> = {
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level, as, weight, className, children, ...props }, ref) => {
    const Tag: HeadingTag = as ?? (`h${level}` as HeadingTag);
    const resolvedWeight = weight ?? defaultWeightByLevel[level];

    return createElement(
      Tag,
      {
        ref,
        className: cn('text-tkf-fg', sizeStyles[level], weightStyles[resolvedWeight], className),
        ...props,
      },
      children
    );
  }
);

Heading.displayName = 'Heading';
